import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { useTranslation } from 'react-i18next'
import styles from './InstallPrompt.module.scss'

const isStandaloneMode = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
}

const isIOSSafariBrowser = () => {
  if (typeof window === 'undefined') {
    return false
  }

  const userAgent = window.navigator.userAgent.toLowerCase()
  const isIOS = /iphone|ipad|ipod/.test(userAgent)
  const isSafari = /safari/.test(userAgent)
  const isOtherIOSBrowser = /crios|fxios|edgios/.test(userAgent)

  return isIOS && isSafari && !isOtherIOSBrowser
}

const InstallPrompt = () => {
  const { t } = useTranslation()
  const [deferredPromptEvent, setDeferredPromptEvent] = useState(null)
  const [isInstalled, setIsInstalled] = useState(isStandaloneMode())
  const [showIOSHint, setShowIOSHint] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault()
      setDeferredPromptEvent(event)
      setShowIOSHint(false)
    }

    const onAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPromptEvent(null)
      setShowIOSHint(false)
    }

    const updateInstallState = () => {
      const standalone = isStandaloneMode()
      setIsInstalled(standalone)

      if (!standalone && isIOSSafariBrowser()) {
        setShowIOSHint(true)
      }
    }

    updateInstallState()

    const displayModeMedia = window.matchMedia('(display-mode: standalone)')
    displayModeMedia?.addEventListener?.('change', updateInstallState)

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)

    return () => {
      displayModeMedia?.removeEventListener?.('change', updateInstallState)
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPromptEvent) {
      return
    }

    deferredPromptEvent.prompt()

    try {
      await deferredPromptEvent.userChoice
    } catch (error) {
      console.error('Install prompt failed:', error)
    }

    setDeferredPromptEvent(null)
  }

  if (isInstalled) {
    return null
  }

  if (!deferredPromptEvent && !showIOSHint) {
    return null
  }

  return (
    <Box className={styles.installPrompt} role="status" aria-live="polite">
      <Typography className={styles.installPrompt__title}>
        {t('Install App CTA')}
      </Typography>
      <Typography className={styles.installPrompt__description}>
        {deferredPromptEvent
          ? t('Install App Description')
          : t('Install App iOS Hint')}
      </Typography>
      {deferredPromptEvent && (
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.installPrompt__button}
          onClick={handleInstallClick}
        >
          {t('Install App Button')}
        </Button>
      )}
    </Box>
  )
}

export default InstallPrompt
